/**
 * Every call to the worker, in one place. Nothing else in the app calls `fetch`.
 *
 * Base URL: `VITE_API_BASE` when set (the deployed worker's origin), else `/api`, which the
 * dev server proxies to `wrangler dev` (see vite.config.ts).
 *
 * This module only talks; it never decides WHETHER to talk. Consent (rule 5) is checked by the
 * callers: the sync client for sync and votes, the shop screen for a panel photo.
 */
import type {
  AvailabilityRating,
  AvailabilityVote,
  FoodDraft,
  RegionCode,
  SyncRecord,
} from '@resupport/core'

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

export class ApiError extends Error {
  /** The worker's error code (`payload_too_large`, `rate_limited`, ...), when it sent one. */
  readonly code: string | undefined
  /** The worker's hint that the same request may succeed later. */
  readonly retryable: boolean | undefined

  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
    const err = isObject(body) && isObject(body.error) ? body.error : undefined
    this.code = typeof err?.code === 'string' ? err.code : undefined
    this.retryable = typeof err?.retryable === 'boolean' ? err.retryable : undefined
  }
  /** No answer at all: offline, DNS, CORS, a dead worker. */
  get offline(): boolean {
    return this.status === 0
  }
}

/**
 * The text to show for a failed response. The worker answers `{ error: { code, message,
 * retryable } }`; a bare `{ error: '...' }` string is accepted too.
 */
export function errorMessageOf(json: unknown, status: number): string {
  const err = isObject(json) ? json.error : undefined
  if (typeof err === 'string' && err) return err
  if (isObject(err) && typeof err.message === 'string' && err.message) return err.message
  return `HTTP ${status}`
}

export interface ApiOptions {
  base?: string
  fetch?: typeof fetch
  /** Bearer token, when there is an identity. */
  token?: string
  /** Default 20 s. A shop with one bar of signal should fail, not hang. */
  timeoutMs?: number
}

function defaultBase(): string {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env
  return (env?.VITE_API_BASE ?? '/api').replace(/\/+$/, '')
}

export interface SyncRequest {
  since?: string
  cursor?: string
  push: SyncRecord[]
}

export type PushOutcome = 'created' | 'updated' | 'unchanged' | 'stale' | 'rejected' | 'failed'

/** The server's answer to one pushed record; `results[i]` answers `push[i]`. */
export interface PushResult {
  kind?: SyncRecord['kind']
  id?: string
  outcome: PushOutcome
  /** The server's copy, when it differs from what was pushed (stale, or a clamped stamp). */
  record?: SyncRecord
  issues?: { path?: string; message: string }[]
}

export interface SyncResponse {
  server_time: string
  records: SyncRecord[]
  cursor?: string
  results?: PushResult[]
}

export interface BarcodeResult {
  draft: FoodDraft
  off_categories: string[]
}

/** The worker's client. Cheap to make; make one per call site with the current token. */
export class Api {
  private readonly base: string
  private readonly fetchFn: typeof fetch
  private readonly token: string | undefined
  private readonly timeoutMs: number

  constructor(opts: ApiOptions = {}) {
    this.base = (opts.base ?? defaultBase()).replace(/\/+$/, '')
    this.fetchFn = opts.fetch ?? ((...args) => fetch(...args))
    this.token = opts.token
    this.timeoutMs = opts.timeoutMs ?? 20_000
  }

  withToken(token: string | undefined): Api {
    return new Api({
      base: this.base,
      fetch: this.fetchFn,
      timeoutMs: this.timeoutMs,
      ...(token ? { token } : {}),
    })
  }

  private async request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (this.token) headers.authorization = `Bearer ${this.token}`
    let payload: BodyInit | undefined
    if (body instanceof FormData) payload = body
    else if (body !== undefined) {
      headers['content-type'] = 'application/json'
      payload = JSON.stringify(body)
    }
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : undefined
    const timer = ctrl ? setTimeout(() => ctrl.abort(), this.timeoutMs) : undefined
    let res: Response
    try {
      res = await this.fetchFn(`${this.base}${path}`, {
        method,
        headers,
        ...(payload !== undefined ? { body: payload } : {}),
        ...(ctrl ? { signal: ctrl.signal } : {}),
      })
    } catch (err) {
      throw new ApiError(err instanceof Error ? err.message : 'Network error', 0)
    } finally {
      if (timer) clearTimeout(timer)
    }
    const text = await res.text()
    let json: unknown = undefined
    try {
      json = text ? JSON.parse(text) : undefined
    } catch {
      json = text
    }
    if (!res.ok) {
      throw new ApiError(errorMessageOf(json, res.status), res.status, json)
    }
    return json as T
  }

  // ------------------------------------------------------------- identity

  /** A new anonymous account for this device. */
  createIdentity(): Promise<{ token: string; account_id: string }> {
    return this.request('POST', '/v1/identity')
  }

  /** A short code another device can type to join this account (needs a token). */
  pairCode(): Promise<{ code: string }> {
    return this.request('POST', '/v1/identity/pair')
  }

  /** Join the account that showed `code`. */
  link(code: string): Promise<{ token: string; account_id?: string }> {
    return this.request('POST', '/v1/identity/link', { code })
  }

  // ----------------------------------------------------------------- sync

  sync(req: SyncRequest): Promise<SyncResponse> {
    return this.request('POST', '/v1/sync', req)
  }

  // --------------------------------------------------------- availability

  pushVotes(votes: AvailabilityVote[]): Promise<unknown> {
    return this.request('POST', '/v1/availability/votes', { votes })
  }

  /**
   * Community ratings for a region, as category → rating. Accepts the worker answering with
   * `{ ratings: {...} }`, a bare `{ category: rating }`, or a full table keyed by region.
   */
  async communityRatings(region: RegionCode): Promise<Record<string, AvailabilityRating>> {
    const raw = await this.request<unknown>(
      'GET',
      `/v1/availability?region=${encodeURIComponent(region)}`,
    )
    const obj = (v: unknown): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null && !Array.isArray(v)
    const src = obj(raw) && obj(raw.ratings) ? raw.ratings : raw
    const out: Record<string, AvailabilityRating> = {}
    if (!obj(src)) return out
    for (const [category, value] of Object.entries(src)) {
      if (!obj(value)) continue
      const rating = typeof value.level === 'number' ? value : value[region]
      if (obj(rating) && typeof rating.level === 'number') {
        out[category] = { ...(rating as unknown as AvailabilityRating), source: 'community' }
      }
    }
    return out
  }

  // ------------------------------------------------------- food lookups

  /** Open Food Facts via the worker. 404 → null (not found is an answer, not an error). */
  async barcode(ean: string): Promise<BarcodeResult | null> {
    try {
      return await this.request<BarcodeResult>('GET', `/v1/barcode/${encodeURIComponent(ean)}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null
      throw err
    }
  }

  /** A nutrition-panel photo read into a draft. The caller must have asked first. */
  panel(image: Blob, filename = 'panel.jpg'): Promise<{ draft: FoodDraft }> {
    const form = new FormData()
    form.append('image', image, filename)
    return this.request('POST', '/v1/panel', form)
  }
}
